import {
	SmsProviderQueueDirection as Direction,
	SmsProviderQueueMessageStatus as MessageStatus,
} from "../generated/django-enums";
import { getGlobalHeadersAsObject } from "../global-headers";
import type { DRFListResponseWrapper } from "../types";
import type { QueueTemplate } from "./email";

export { Direction, MessageStatus };

export type SmsSentQueueSearchParams = {
	id?: number;
	user__id?: number;
	person__id?: number;
	assignment__id?: number;
	from_template__name?: string;
	up_group?: number | "all";
	sent?: boolean;
	error_isnull?: boolean;
	send_immediately?: boolean;
	manually?: boolean;
	limit?: number;
	offset?: number;
	ordering?: string;
};

export type SmsSentQueue = {
	manually: boolean;
	message_text: string;
	send_immediately: boolean;
	provider_message?: number;
	user: number;
	person: number;
	personphonenumber?: number; // Id in db
	personphonenumber_str?: string;
	from_template?: number;
	assignment?: number;
	vote?: number;
};

export type SmsSentQueueResponse = SmsSentQueue & {
	created: string;
	updated: string;
	person_name?: string;
	person_url?: string;
	assignment_name?: string;
	template_name?: string;
	/** Who wrote the pitch: the AI prompt version, else the template. */
	pitch_source_name?: string | null;
	id: number;
};

export type SmsProviderQueue = {
	id?: number;
	created?: string;
	from_phonenumber: string;
	to_phonenumber: string;
	message_text: string;
	provider_message_id: string;
	status?: MessageStatus;
	direction: Direction;
	delivered?: string;
	cost?: number;
	person?: number;
	user?: number;
};

export const addSmsToQueue = (data: SmsSentQueue): Promise<SmsSentQueueResponse> => {
	return fetch("/api/v2/sms_sent_queue/", {
		method: "POST",
		body: JSON.stringify(data),
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
	}).then((res) => {
		if (res.status !== 201) {
			throw new Error("Could not send sms");
		}
		return res.json();
	});
};

export const getSmsQueueForPerson = (
	personId: number
): Promise<DRFListResponseWrapper<SmsSentQueueResponse[]>> => {
	const params = new URLSearchParams();
	if (personId) {
		params.append("person", personId.toString());
	}
	return fetch(`/api/v2/sms_sent_queue/?${params.toString()}`, {
		method: "GET",
		headers: {
			...getGlobalHeadersAsObject(),
		},
	}).then((res) => {
		if (res.status !== 200) {
			throw new Error("Could not send sms");
		}
		return res.json();
	});
};

export const getSmsProviderQueueByMessageId = ({
	providerMessageId,
	personId,
}: {
	providerMessageId?: string;
	personId?: number;
}): Promise<DRFListResponseWrapper<SmsProviderQueue[]>> => {
	const params = new URLSearchParams();

	if (providerMessageId) {
		params.append("provider_message_id", providerMessageId);
	}
	if (personId) {
		params.append("person_id", personId.toString());
	}

	return fetch(`/api/v2/sms_provider_queue/?${params.toString()}`, {
		method: "GET",
		headers: {
			...getGlobalHeadersAsObject(),
		},
	}).then((res) => {
		if (res.status !== 200) {
			throw new Error("Could not retrieve conversation");
		}
		return res.json();
	});
};

export type QueueAssignment = {
	id: number;
	name: string;
	group_name: string | null;
	count: number;
};

export type QueueUser = {
	id: number;
	username: string;
	count: number;
};

export const getSmsQueueDistinctUsers = (
	params?: Partial<SmsSentQueueSearchParams>
): Promise<QueueUser[]> => {
	const searchParams = new URLSearchParams();
	if (params) {
		for (const [key, val] of Object.entries(params)) {
			if (val == null) return;
			let newKey = key;
			if (key === "person__id") newKey = "person";
			if (key === "user__id") newKey = "user";
			if (key === "assignment__id") newKey = "assignment";
			searchParams.set(newKey, String(val));
		}
	}
	const qs = searchParams.toString();
	return fetch(`/api/v2/sms_sent_queue/distinct_users/${qs ? `?${qs}` : ""}`, {
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

export const getSmsQueueDistinctAssignments = (
	params?: Partial<SmsSentQueueSearchParams>
): Promise<QueueAssignment[]> => {
	const searchParams = new URLSearchParams();
	if (params) {
		for (const [key, val] of Object.entries(params)) {
			if (val == null) return;
			let newKey = key;
			if (key === "person__id") newKey = "person";
			if (key === "user__id") newKey = "user";
			if (key === "assignment__id") newKey = "assignment";
			searchParams.set(newKey, String(val));
		}
	}
	const qs = searchParams.toString();
	return fetch(`/api/v2/sms_sent_queue/distinct_assignments/${qs ? `?${qs}` : ""}`, {
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

export const getSmsQueueDistinctTemplates = (
	params?: Partial<SmsSentQueueSearchParams>
): Promise<QueueTemplate[]> => {
	const searchParams = new URLSearchParams();
	if (params) {
		for (const [key, val] of Object.entries(params)) {
			if (val == null) return;
			let newKey = key;
			if (key === "person__id") newKey = "person";
			if (key === "user__id") newKey = "user";
			if (key === "assignment__id") newKey = "assignment";
			searchParams.set(newKey, String(val));
		}
	}
	const qs = searchParams.toString();
	return fetch(`/api/v2/sms_sent_queue/distinct_templates/${qs ? `?${qs}` : ""}`, {
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

export const getSmsSentQueue = (
	params: SmsSentQueueSearchParams
): Promise<DRFListResponseWrapper<SmsSentQueueResponse[]>> => {
	const queryParams = Object.keys(params).map((key) => {
		let newKey = key;
		if (key === "person__id") {
			newKey = "person";
		}
		if (key === "user__id") {
			newKey = "user";
		}
		if (key === "assignment__id") {
			newKey = "assignment";
		}
		return [newKey, params[key]];
	});
	return fetch(
		`/api/v2/sms_sent_queue/?${new URLSearchParams(queryParams).toString()}`,
		{
			method: "GET",
			headers: getGlobalHeadersAsObject(),
		}
	).then(async (res) => {
		if (res.ok) return res.json();
		throw new Error(await res.json());
	});
};

export const deleteSmsSentQueueById = (id: number): Promise<any> => {
	return fetch(`/api/v2/sms_sent_queue/${id}/`, {
		method: "DELETE",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => {
		console.log(res);
	});
};

export const bulkDeleteSmsSentQueue = (ids: number[]): Promise<{ deleted: number }> => {
	return fetch("/api/v2/sms_sent_queue/bulk_delete/", {
		method: "POST",
		body: JSON.stringify({ ids }),
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
	}).then((res) => res.json());
};
